/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { ReactNode, useEffect, useState } from 'react';
import {
    fetchAvailableWidgetTypes,
    getDefaultWidgetTypes,
    initializeWidgetRegistry,
    WidgetType,
} from './widget-registry';
import httpRequest from './http-request';
import { DashboardContext, DashboardContextType } from '../contexts/dashboard-context';

/**
 * Updated Dashboard Context - With support for dynamic widget types from backend
 * Uses UUIDv7-based widget type references instead of fixed enums
 */

export type WidgetSize = '1x1' | '1x2' | '2x2' | '2x4' | '3x4';

export interface WidgetItem {
    id: string;
    layoutId: string;
    widgetTypeId: string; // FK to widget_type.id (UUIDv7)
    widgetType?: WidgetType; // Populated from backend
    widgetSize: WidgetSize;
    gridX: number;
    gridY: number;
    config?: Record<string, unknown>;
    zOrder: number;
    isVisible: boolean;
    isLocked: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface WidgetLayout {
    id: string;
    userId: string;
    name: string;
    description?: string;
    isActive: boolean;
    isDefault: boolean;
    layoutVersion: number;
    items: WidgetItem[];
    createdAt?: string;
    updatedAt?: string;
}

function normalizeLayout(layout: unknown): WidgetLayout | null {
    if (!layout || typeof layout !== 'object') return null;

    const rawLayout = layout as Partial<WidgetLayout> & { items?: unknown };

    return {
        id: rawLayout.id ?? '',
        userId: rawLayout.userId ?? '',
        name: rawLayout.name ?? '',
        description: rawLayout.description,
        isActive: Boolean(rawLayout.isActive),
        isDefault: Boolean(rawLayout.isDefault),
        layoutVersion: typeof rawLayout.layoutVersion === 'number' ? rawLayout.layoutVersion : 1,
        items: Array.isArray(rawLayout.items) ? rawLayout.items : [],
        createdAt: rawLayout.createdAt,
        updatedAt: rawLayout.updatedAt,
    };
}

// DashboardContext and related types live in src/contexts/dashboard-context.tsx

export function DashboardProvider({ children }: { children: ReactNode }) {
    // Widget type state
    const [availableWidgetTypes, setAvailableWidgetTypes] = useState<WidgetType[]>([]);
    const [loadingWidgetTypes, setLoadingWidgetTypes] = useState(true);

    // Layout state
    const [layout, setLayout] = useState<WidgetLayout | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch available widget types from backend on mount
    useEffect(() => {
        // Initialize widget registry
        initializeWidgetRegistry();

        const loadWidgetTypes = async () => {
            setLoadingWidgetTypes(true);
            try {
                const types = await fetchAvailableWidgetTypes();
                setAvailableWidgetTypes(types.length > 0 ? types : getDefaultWidgetTypes());
            } catch (err) {
                console.error('Failed to load widget types:', err);
                setAvailableWidgetTypes(getDefaultWidgetTypes());
            } finally {
                setLoadingWidgetTypes(false);
            }
        };

        void loadWidgetTypes();
    }, []);

    // Fetch initial layout
    useEffect(() => {
        void fetchLayout();
    }, []);

    const fetchLayout = async () => {
        setLoading(true);
        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            console.log('Fetching layout from:', `${apiBase}/dashboard/layout`);
            const response = await httpRequest(`${apiBase}/dashboard/layout`, 'GET');

            if (!response.ok) {
                // 404 means no layout exists yet - create a default one
                if (response.status === 404) {
                    console.log('No layout found, creating default layout...');
                    const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
                    const createResponse = await httpRequest(`${apiBase}/dashboard/layout`, 'POST', {
                        name: 'My Dashboard',
                        description: 'Default dashboard layout',
                        isDefault: true,
                    });

                    if (createResponse.ok) {
                        const newData = await createResponse.json();
                        setLayout(normalizeLayout(newData.data));
                        setError(null);
                    } else {
                        throw new Error('Failed to create default layout');
                    }
                } else {
                    throw new Error(`Failed to fetch layout: ${response.status}`);
                }
            } else {
                const data = await response.json();
                setLayout(normalizeLayout(data.data));
                setError(null);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load dashboard';
            setError(message);
            console.error('Error fetching layout:', err);
        } finally {
            setLoading(false);
        }
    };

    const updateWidgetPosition = async (widgetId: string, gridX: number, gridY: number) => {
        if (!layout) return;

        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            const response = await httpRequest(`${apiBase}/dashboard/widget/${widgetId}`, 'PUT', { gridX, gridY });

            if (response.ok) {
                // Update local state optimistically
                setLayout((prev) =>
                    prev
                        ? {
                              ...prev,
                              items: prev.items.map((item) =>
                                  item.id === widgetId ? { ...item, gridX, gridY } : item
                              ),
                          }
                        : null
                );
            }
        } catch (err) {
            console.error('Error updating widget position:', err);
        }
    };

    const updateWidget = async (widgetId: string, updates: Partial<WidgetItem>) => {
        if (!layout) return;

        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            const response = await httpRequest(`${apiBase}/dashboard/widget/${widgetId}`, 'PUT', updates);

            if (response.ok) {
                setLayout((prev) =>
                    prev
                        ? {
                              ...prev,
                              items: prev.items.map((item) => (item.id === widgetId ? { ...item, ...updates } : item)),
                          }
                        : null
                );
            }
        } catch (err) {
            console.error('Error updating widget:', err);
        }
    };

    const deleteWidget = async (widgetId: string) => {
        if (!layout) return;

        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            const response = await httpRequest(`${apiBase}/dashboard/widget/${widgetId}`, 'DELETE');

            if (response.ok) {
                setLayout((prev) =>
                    prev
                        ? {
                              ...prev,
                              items: prev.items.filter((item) => item.id !== widgetId),
                          }
                        : null
                );
            }
        } catch (err) {
            console.error('Error deleting widget:', err);
        }
    };

    const addWidget = async (widget: Partial<WidgetItem>) => {
        if (!layout) return;

        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            const response = await httpRequest(`${apiBase}/dashboard/layout/${layout.id}/widget`, 'POST', widget);

            if (response.ok) {
                const data = await response.json();
                const newItem = data.data as WidgetItem;
                setLayout((prev) =>
                    prev
                        ? {
                              ...prev,
                              items: Array.isArray(prev.items) ? [...prev.items, newItem] : [newItem],
                          }
                        : null
                );
            }
        } catch (err) {
            console.error('Error adding widget:', err);
        }
    };

    const saveLayout = async () => {
        if (!layout) return;

        try {
            const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
            await httpRequest(`${apiBase}/dashboard/layout/${layout.id}`, 'PUT', {
                name: layout.name,
                description: layout.description,
                items: layout.items,
            });
        } catch (err) {
            console.error('Error saving layout:', err);
        }
    };

    const value: DashboardContextType = {
        availableWidgetTypes,
        loadingWidgetTypes,
        layout,
        loading,
        error,
        fetchLayout,
        updateWidgetPosition,
        updateWidget,
        deleteWidget,
        addWidget,
        saveLayout,
    };

    return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

// useDashboard hook is exported from the dashboard context file
